/**
 * Terra CodeMirror Theme
 *
 * Custom light and dark themes for the HTTP editor.
 * Provides syntax highlighting colors optimized for HTTP content readability.
 */

import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import type { Extension } from '@codemirror/state';
import { token } from '@terra/ui-styled-system';

export const HTTP_EDITOR_LIGHT_BACKGROUND = '#fff';
export const HTTP_EDITOR_DARK_BACKGROUND = 'var(--colors-bg-default)';

export const HTTP_COLORS_LIGHT = {
  // HTTP-specific (GitHub Light palette)
  method: '#d73a49',
  url: '#032f62',
  version: '#6a737d',
  statusCode: '#005cc5',
  statusText: '#24292e',

  // Header
  headerName: '#6f42c1',
  headerValue: '#032f62',

  // Cookie tokens (distinct from generic headers)
  cookieKey: '#9a6700',
  cookieValue: '#1a7f37',

  // Auth tokens
  authType: '#cf222e',
  authToken: '#0550ae',

  // Status code categories
  statusSuccess: '#1a7f37',
  statusRedirect: '#005cc5',
  statusClientError: '#e36209',
  statusServerError: '#d73a49',

  // General syntax
  punctuation: '#6a737d',
  string: '#032f62',
  number: '#005cc5',
  property: '#6f42c1',
  boolean: '#e36209',

  // Editor chrome
  background: HTTP_EDITOR_LIGHT_BACKGROUND,
  foreground: '#24292e',
  selection: '#BBDFFF',
  cursor: '#24292e',
  gutterBackground: HTTP_EDITOR_LIGHT_BACKGROUND,
  gutterForeground: '#6e7781',
  gutterBorder: '#e1e4e8',
  lineHighlight: '#f6f8fa',

  // Search highlighting
  searchMatch: '#fff8c5',
  searchMatchSelected: '#ffdf5d',
  searchMatchSelectedOutline: '#d4a72c',
} as const;

export const HTTP_COLORS_DARK = {
  // HTTP-specific (GitHub Dark palette)
  method: '#ff7b72',
  url: '#a5d6ff',
  version: '#8b949e',
  statusCode: '#79c0ff',
  statusText: '#c9d1d9',

  // Header
  headerName: '#d2a8ff',
  headerValue: '#a5d6ff',

  // Cookie tokens (distinct from generic headers)
  cookieKey: '#ffa657',
  cookieValue: '#7ee787',

  // Auth tokens
  authType: '#ffa198',
  authToken: '#a5d6ff',

  // Status code categories
  statusSuccess: '#7ee787',
  statusRedirect: '#79c0ff',
  statusClientError: '#ffa657',
  statusServerError: '#ff7b72',

  // General syntax
  punctuation: '#8b949e',
  string: '#a5d6ff',
  number: '#79c0ff',
  property: '#d2a8ff',
  boolean: '#ffab70',

  // Editor chrome
  background: HTTP_EDITOR_DARK_BACKGROUND,
  foreground: '#c9d1d9',
  selection: '#003d73',
  cursor: '#c9d1d9',
  gutterBackground: HTTP_EDITOR_DARK_BACKGROUND,
  gutterForeground: '#8b949e',
  gutterBorder: '#30363d',
  lineHighlight: '#36334280',

  // Search highlighting
  searchMatch: '#e2b73480',
  searchMatchSelected: '#d29922',
  searchMatchSelectedOutline: '#bb8009',
} as const;

export const IDE_COLORS = {
  method: token('colors.fg.decorative.red'),
  url: token('colors.fg.decorative.blue'),
  version: token('colors.fg.muted'),
  statusCode: token('colors.fg.decorative.blue'),
  statusText: token('colors.fg.default'),
  headerName: token('colors.fg.decorative.purple'),
  headerValue: token('colors.fg.default'),
  cookieKey: token('colors.fg.decorative.orange'),
  cookieValue: token('colors.fg.decorative.green'),
  authType: token('colors.fg.error'),
  authToken: token('colors.fg.decorative.blue'),
  statusSuccess: token('colors.fg.success'),
  statusRedirect: token('colors.fg.info'),
  statusClientError: token('colors.fg.warning'),
  statusServerError: token('colors.fg.error'),
  punctuation: token('colors.fg.muted'),
  string: token('colors.fg.decorative.green'),
  number: token('colors.fg.decorative.blue'),
  property: token('colors.fg.decorative.purple'),
  boolean: token('colors.fg.decorative.orange'),
  background: token('colors.bg.default'),
  foreground: token('colors.fg.default'),
  selection: token('colors.bg.accent.light'),
  cursor: token('colors.fg.default'),
  gutterBackground: token('colors.bg.default'),
  gutterForeground: token('colors.fg.muted'),
  gutterBorder: token('colors.border.subtle'),
  lineHighlight: token('colors.bg.subtle'),
  searchMatch: token('colors.bg.warning'),
  searchMatchSelected: token('colors.bg.warning'),
  searchMatchSelectedOutline: token('colors.border.warning'),
} as const;

/** Color palette type for theme configuration */
export type HttpColorPalette = {
  readonly method: string;
  readonly url: string;
  readonly version: string;
  readonly statusCode: string;
  readonly statusText: string;
  readonly headerName: string;
  readonly headerValue: string;
  readonly cookieKey: string;
  readonly cookieValue: string;
  readonly authType: string;
  readonly authToken: string;
  readonly statusSuccess: string;
  readonly statusRedirect: string;
  readonly statusClientError: string;
  readonly statusServerError: string;
  readonly punctuation: string;
  readonly string: string;
  readonly number: string;
  readonly property: string;
  readonly boolean: string;
  readonly background: string;
  readonly foreground: string;
  readonly selection: string;
  readonly cursor: string;
  readonly gutterBackground: string;
  readonly gutterForeground: string;
  readonly gutterBorder: string;
  readonly lineHighlight: string;
  readonly searchMatch: string;
  readonly searchMatchSelected: string;
  readonly searchMatchSelectedOutline: string;
};

// =============================================================================
// Shared Style Configuration
// =============================================================================

/** Base font size for editor content */
const FONT_SIZE = '12px';

/** Line height for editor content */
const LINE_HEIGHT = '1.6';

// =============================================================================
// Editor Themes
// =============================================================================

/**
 * Creates editor chrome styles (non-syntax elements).
 */
function createEditorTheme(
  colors: HttpColorPalette,
  isDark: boolean,
): Extension {
  return EditorView.theme(
    {
      '&': {
        backgroundColor: colors.background,
        color: colors.foreground,
      },

      // Content area
      '.cm-content': {
        fontFamily: token('fonts.mono'),
        fontSize: FONT_SIZE,
        lineHeight: LINE_HEIGHT,
        caretColor: colors.cursor,
      },

      // Cursor
      '.cm-cursor, .cm-dropCursor': {
        borderLeftColor: colors.cursor,
      },

      // Selection
      '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
        {
          backgroundColor: colors.selection,
        },

      // Active line highlight
      '.cm-activeLine': {
        backgroundColor: colors.lineHighlight,
      },

      // Line numbers gutter
      '.cm-gutters': {
        backgroundColor: colors.gutterBackground,
        color: colors.gutterForeground,
        borderRight: 'none',
      },

      // Active line gutter
      '.cm-activeLineGutter': {
        backgroundColor: colors.lineHighlight,
      },

      // Scroller
      '.cm-scroller': {
        overflow: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--colors-border-outline) transparent',
      },

      // Line padding
      '.cm-line': {
        padding: '0 8px',
      },

      // Remove unwanted text decorations from syntax spans,
      // but allow decorator inline styles to override via [style]
      '.cm-line span:not([style])': {
        textDecoration: 'none !important',
      },

      // Search match highlighting
      '.cm-searchMatch': {
        backgroundColor: colors.searchMatch,
        borderRadius: '2px',
      },

      // Current search match highlighting
      '.cm-searchMatch-selected': {
        backgroundColor: colors.searchMatchSelected,
        outline: `2px solid ${colors.searchMatchSelectedOutline}`,
        outlineOffset: '-1px',
      },
    },
    { dark: isDark },
  );
}

const lightEditorTheme = createEditorTheme(HTTP_COLORS_LIGHT, false);
const darkEditorTheme = createEditorTheme(HTTP_COLORS_DARK, true);

// =============================================================================
// Syntax Highlighting Styles
// =============================================================================

/**
 * Creates syntax highlighting style definitions.
 */
function createHighlightStyle(colors: HttpColorPalette): HighlightStyle {
  return HighlightStyle.define([
    // HTTP-specific
    { tag: t.keyword, color: colors.method, fontWeight: '700' },
    { tag: t.url, color: colors.url, textDecoration: 'underline' },
    { tag: t.meta, color: colors.string },
    { tag: t.number, color: colors.number },

    // Headers
    { tag: t.propertyName, color: colors.headerName, fontWeight: '500' },
    { tag: t.attributeName, color: colors.headerName, fontWeight: '500' },
    { tag: t.attributeValue, color: colors.headerValue },

    // Values
    { tag: t.string, color: colors.string },
    { tag: [t.bool, t.null, t.atom], color: colors.boolean },

    // Punctuation
    { tag: [t.punctuation, t.brace, t.bracket], color: colors.punctuation },
    { tag: t.operator, color: colors.number },

    // General
    { tag: t.content, color: colors.foreground },
    { tag: t.comment, color: colors.version, fontStyle: 'italic' },

    // HTML/XML
    { tag: [t.standard(t.tagName), t.tagName], color: colors.method },
    { tag: t.className, color: colors.headerName },

    // Names
    { tag: [t.name, t.quote], color: colors.method },
    {
      tag: [t.heading, t.strong],
      color: colors.headerName,
      fontWeight: 'bold',
    },
    { tag: t.emphasis, color: colors.headerName, fontStyle: 'italic' },

    // JavaScript / general code
    { tag: t.function(t.variableName), color: colors.headerName },
    { tag: t.definition(t.variableName), color: colors.headerName },
    { tag: t.variableName, color: colors.number },
    { tag: [t.typeName, t.typeOperator], color: colors.method },

    // Links
    { tag: t.link, textDecoration: 'underline' },
    { tag: t.invalid, color: colors.method },
  ]);
}

const lightHighlightStyle = createHighlightStyle(HTTP_COLORS_LIGHT);
const darkHighlightStyle = createHighlightStyle(HTTP_COLORS_DARK);
const ideHighlightStyle = createHighlightStyle(IDE_COLORS);

const ideLightEditorTheme = createEditorTheme(IDE_COLORS, false);
const ideDarkEditorTheme = createEditorTheme(IDE_COLORS, true);

// =============================================================================
// Exported Themes
// =============================================================================

/**
 * Complete light theme extension for the HTTP editor.
 * Includes editor chrome and syntax highlighting.
 */
export const terraLightTheme: Extension = [
  lightEditorTheme,
  syntaxHighlighting(lightHighlightStyle),
];

/**
 * Complete dark theme extension for the HTTP editor.
 * Includes editor chrome and syntax highlighting.
 */
export const terraDarkTheme: Extension = [
  darkEditorTheme,
  syntaxHighlighting(darkHighlightStyle),
];

export const terraIdeLightTheme: Extension = [
  ideLightEditorTheme,
  syntaxHighlighting(ideHighlightStyle),
];

export const terraIdeDarkTheme: Extension = [
  ideDarkEditorTheme,
  syntaxHighlighting(ideHighlightStyle),
];

/**
 * Base theme with minimal styling.
 * Can be used as a foundation when building custom themes.
 */
export const terraBaseTheme = EditorView.baseTheme({
  '.cm-content': {
    fontFamily: token('fonts.mono'),
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
  },
  '.cm-scroller': {
    overflow: 'auto',
  },
  '.cm-line': {
    padding: '0 8px',
  },
  '.cm-line span:not([style])': {
    textDecoration: 'none !important',
  },
});

export function resolveTerraEditorTheme({
  theme,
  syntaxPalette,
}: {
  theme: 'light' | 'dark';
  syntaxPalette?: 'http' | 'ide';
}): Extension {
  const isDark = theme === 'dark';

  if (syntaxPalette === 'ide') {
    return isDark ? terraIdeDarkTheme : terraIdeLightTheme;
  }

  return isDark ? terraDarkTheme : terraLightTheme;
}
